import { useMemo, useRef, useState } from "react";
import { dayKey, catMeta, isCancelled } from "@/lib/catalysts";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const labelFor = (key, todayKey) => {
  const d = new Date(key + "T00:00:00");
  const t = new Date(todayKey + "T00:00:00");
  const diff = Math.round((d - t) / 86400000);
  const base = `${WD[d.getDay()]} · ${MO[d.getMonth()]} ${d.getDate()}`;
  if (diff === 0) return { head: "Today", sub: base };
  if (diff === 1) return { head: "Tomorrow", sub: base };
  if (diff < 7) return { head: WD[d.getDay()], sub: `${MO[d.getMonth()]} ${d.getDate()}` };
  return { head: `${MO[d.getMonth()]} ${d.getDate()}`, sub: WD[d.getDay()] };
};

/** FOMO-style "Up Next" feed: 14-day week strip on top, events grouped by day below. */
export default function UpNextList({ upcoming, tbd, renderRow, todayKey, locked }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const e of upcoming) { const k = dayKey(e.date); if (!m.has(k)) m.set(k, []); m.get(k).push(e); }
    return [...m.entries()];
  }, [upcoming]);

  const strip = useMemo(() => {
    const t = new Date(todayKey + "T00:00:00");
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(t); d.setDate(t.getDate() + i);
      const k = dayKey(d);
      const evs = (groups.find(([gk]) => gk === k) || [, []])[1];
      return { key: k, d, count: evs.length, colors: [...new Set(evs.filter((e) => !isCancelled(e)).map((e) => catMeta(e.category).color))].slice(0, 4) };
    });
  }, [groups, todayKey]);

  const [sel, setSel] = useState(null);
  const refs = useRef({});
  const pick = (k) => { setSel(k); refs.current[k]?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div data-testid="upnext-view" className={locked ? "blur-[4px] select-none pointer-events-none" : ""} aria-hidden={locked}>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-3 snap-x-row" data-testid="week-strip">
        {strip.map(({ key, d, count, colors }) => {
          const isToday = key === todayKey;
          const active = sel === key;
          return (
            <button key={key} onClick={() => pick(key)} disabled={count === 0} data-testid={`strip-day-${key}`}
              className={`tap shrink-0 w-[52px] rounded-2xl border px-1 py-2 flex flex-col items-center gap-1 transition-colors ${
                active ? "border-emerald-500/60 bg-emerald-500/12" : isToday ? "border-[#334566] bg-[#172030]" : "border-[#222F43] bg-[#111723]"} ${count === 0 ? "opacity-40" : ""}`}>
              <span className={`text-[9px] font-mono uppercase ${isToday ? "text-emerald-400" : "text-slate-500"}`}>{isToday ? "today" : WD[d.getDay()]}</span>
              <span className={`font-display font-extrabold text-lg leading-none ${active ? "text-emerald-400" : "text-slate-100"}`}>{d.getDate()}</span>
              <span className="flex gap-0.5 h-1.5">
                {colors.map((c) => <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />)}
                {count === 0 && <span className="h-1.5 w-1.5 rounded-full bg-[#222F43]" />}
              </span>
            </button>
          );
        })}
      </div>

      {groups.length === 0 && <div className="text-center py-16 text-slate-500">No upcoming catalysts yet.</div>}
      <div className="space-y-5">
        {groups.map(([k, evs]) => {
          const { head, sub } = labelFor(k, todayKey);
          return (
            <section key={k} ref={(el) => { refs.current[k] = el; }} className="scroll-mt-[4.5rem]" data-testid={`day-group-${k}`}>
              <div className="flex items-baseline gap-2 mb-2 px-0.5">
                <h3 className={`font-display font-extrabold text-base ${k === todayKey ? "text-emerald-400" : "text-slate-100"}`}>{head}</h3>
                <span className="text-[11px] font-mono text-slate-500">{sub}</span>
                <span className="ml-auto text-[10px] font-mono text-slate-600">{evs.length} event{evs.length > 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-2">{evs.map(renderRow)}</div>
            </section>
          );
        })}
      </div>
      {tbd}
    </div>
  );
}
